import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ProtectedRoute from "@/components/ProtectedRoute";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30000, retry: 1 } },
});
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import SchedulePage from "@/pages/SchedulePage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import GroupsPage from "@/pages/GroupsPage";
import RevenuePage from "@/pages/RevenuePage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import SettingsPage from "@/pages/SettingsPage";
import CustomerBookingPage from "@/pages/CustomerBookingPage";
import CustomerProfilePage from "@/pages/CustomerProfilePage";
import LoyaltyPage from "@/pages/LoyaltyPage";
import CheckinPage from "@/pages/CheckinPage";
import POSPage from "@/pages/POSPage";
import ProductsPage from "@/pages/ProductsPage";
import WarehousePage from "@/pages/WarehousePage";
import CopilotPage from "@/pages/CopilotPage";
import AccountingPage from "@/pages/AccountingPage";
import QuotesPage from "@/pages/QuotesPage";
import KiotVietPage from "@/pages/KiotVietPage";
import DiscordPage from "@/pages/DiscordPage";
import KiotVietMegaPage from "@/pages/KiotVietMegaPage";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

function HomeRedirect() {
  const { user, loading } = useAuth();
  // While checking auth, show nothing (prevent flash)
  if (loading) return null;
  // On production (non-preview), always redirect to /dat-san for unauthenticated users
  if (!user) return <Navigate to="/dat-san" replace />;
  if (user.role === "customer") return <Navigate to="/dat-san" replace />;
  return <Layout><SchedulePage /></Layout>;
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <DashboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={<HomeRedirect />}
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <CustomersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <CustomerDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups"
            element={
              <ProtectedRoute allowedRoles={["ceo", "staff", "employee"]}>
                <Layout>
                  <GroupsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/revenue"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <RevenuePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <AnalyticsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/loyalty"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <LoyaltyPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/check-in"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <CheckinPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/ai-engine" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/pos"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <POSPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <ProductsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/warehouse"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <WarehousePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <NotificationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dat-san"
            element={
              <Layout>
                <CustomerBookingPage />
              </Layout>
            }
          />
          <Route
            path="/tai-khoan"
            element={
              <ProtectedRoute allowedRoles={["customer"]}>
                <Layout>
                  <CustomerProfilePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/copilot"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <CopilotPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <AccountingPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <QuotesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/discord"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <DiscordPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/kiotviet"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <KiotVietPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/kiotviet-ops"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <Layout>
                  <KiotVietMegaPage />
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
    </ThemeProvider>
    </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
